import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start auditing websites in seconds. It's free."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" data-testid="go-to-login" className="text-klein font-semibold hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            data-testid="register-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="mt-1.5"
            placeholder="Jane Doe"
          />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="register-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1.5"
            placeholder="you@company.com"
          />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            data-testid="register-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="mt-1.5"
            placeholder="At least 6 characters"
          />
        </div>
        {error && (
          <p data-testid="register-error" className="text-sm text-bad font-medium">
            {error}
          </p>
        )}
        <Button
          type="submit"
          data-testid="register-submit"
          disabled={loading}
          className="w-full bg-[#0A0A0A] hover:bg-gray-800 py-6 text-base"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
